import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { getProfileImageUrl } from '@/lib/utils';
import DashboardLayout from '@/components/layout/DashboardLayout';
import BackButton from '@/components/BackButton';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Send, ArrowLeft, MessageSquare, Check, X, Bell, Flag } from 'lucide-react';
import { ReportButton } from '@/components/ReportDialog';

const API_URL = window.location.origin;

const MessagesPage = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [dmRequests, setDmRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    fetchDmRequests();
  }, []);

  useEffect(() => {
    if (partnerId) {
      fetchMessages(partnerId);
      const partner = conversations.find(c => c.partner?.id === partnerId);
      if (partner) {
        setSelectedPartner(partner.partner);
      }
    }
  }, [partnerId, conversations]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/messages`);
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDmRequests = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dm/requests`);
      setDmRequests(response.data.incoming || []);
    } catch (error) {
      console.error('Failed to fetch DM requests:', error);
    }
  };

  const handleDmResponse = async (requestId, accept) => {
    setRespondingTo(requestId);
    try {
      const response = await axios.post(
        `${API_URL}/api/dm/requests/${requestId}/respond?accept=${accept}`
      );
      
      if (accept) {
        toast.success('Request accepted! You can now message each other.');
        // Refresh conversations and navigate to the new chat
        await fetchConversations();
        if (response.data.conversation_id) {
          navigate(`/messages/${response.data.sender_id}`);
        }
      } else {
        toast.info('Request declined');
      }
      
      // Remove from pending requests
      setDmRequests(prev => prev.filter(r => r.id !== requestId));
    } catch (error) {
      toast.error('Failed to respond to request');
    } finally {
      setRespondingTo(null);
    }
  };

  const fetchMessages = async (partnerIdToFetch) => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/${partnerIdToFetch}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !partnerId) return;

    setSending(true);
    try {
      const response = await axios.post(`${API_URL}/api/messages`, {
        receiver_id: partnerId,
        content: newMessage.trim()
      });
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString();
  };

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="messages-page">
        <div className="p-4 md:hidden">
          <BackButton to="/dashboard" label="Back to Dashboard" />
        </div>
        
        <div className="flex flex-1">
        {/* Conversations List */}
        <div className={`w-full md:w-80 border-r border-sand bg-white flex flex-col ${partnerId ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-sand">
            <h1 className="font-heading text-xl font-semibold text-forest">Messages</h1>
          </div>
          
          <ScrollArea className="flex-1">
            {/* Pending DM Requests Section */}
            {dmRequests.length > 0 && (
              <div className="p-4 bg-terracotta/5 border-b border-terracotta/20">
                <div className="flex items-center gap-2 mb-3">
                  <Bell className="h-4 w-4 text-terracotta" />
                  <h2 className="font-medium text-terracotta text-sm">
                    Message Requests ({dmRequests.length})
                  </h2>
                </div>
                <div className="space-y-3">
                  {dmRequests.map((request) => (
                    <div key={request.id} className="p-3 bg-white rounded-lg border border-sand shadow-sm">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={getProfileImageUrl(request.sender?.profile_image)} />
                          <AvatarFallback className="bg-terracotta/20 text-terracotta">
                            {request.sender?.full_name?.charAt(0) || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-forest text-sm truncate">
                            {request.sender?.full_name || 'Member'}
                          </p>
                          <p className="text-xs text-gray-500">wants to message you</p>
                          {request.initial_message && (
                            <p className="mt-1 text-xs text-gray-600 bg-sand/50 p-2 rounded italic line-clamp-2">
                              "{request.initial_message}"
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={() => handleDmResponse(request.id, true)}
                          disabled={respondingTo === request.id}
                          className="flex-1 h-8 bg-forest hover:bg-forest/90 text-white text-xs"
                        >
                          <Check className="h-3 w-3 mr-1" /> Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDmResponse(request.id, false)}
                          disabled={respondingTo === request.id}
                          className="flex-1 h-8 text-xs"
                        >
                          <X className="h-3 w-3 mr-1" /> Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {loading ? (
              <div className="p-4 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3 animate-pulse">
                    <div className="h-12 w-12 rounded-full bg-sand" />
                    <div className="flex-1">
                      <div className="h-4 bg-sand rounded w-24 mb-2" />
                      <div className="h-3 bg-sand rounded w-32" />
                    </div>
                  </div>
                ))}
              </div>
            ) : conversations.length === 0 && dmRequests.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No conversations yet</p>
                <p className="text-sm text-gray-400 mt-2">Start a conversation from a coach profile or the member directory</p>
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No conversations yet</p>
                <p className="text-sm text-gray-400 mt-2">Accept a message request above to start chatting</p>
              </div>
            ) : (
              <div className="divide-y divide-sand">
                {conversations.map((conv) => (
                  <Link 
                    key={conv.partner?.id} 
                    to={`/messages/${conv.partner?.id}`}
                    className={`flex items-center gap-3 p-4 hover:bg-sand/50 transition-colors ${
                      partnerId === conv.partner?.id ? 'bg-sand' : ''
                    }`}
                    data-testid={`conversation-${conv.partner?.id}`}
                  >
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={getProfileImageUrl(conv.partner?.profile_image)} />
                      <AvatarFallback className="bg-forest text-white">
                        {conv.partner?.full_name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-forest truncate">{conv.partner?.full_name}</h3>
                        {conv.last_message && (
                          <span className="text-xs text-gray-400">
                            {formatTime(conv.last_message.created_at)}
                          </span>
                        )}
                      </div>
                      {conv.last_message && (
                        <p className="text-sm text-gray-500 truncate">{conv.last_message.content}</p>
                      )}
                    </div>
                    {conv.unread_count > 0 && (
                      <span className="h-5 w-5 rounded-full bg-terracotta text-white text-xs flex items-center justify-center">
                        {conv.unread_count}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col bg-cream ${!partnerId ? 'hidden md:flex' : 'flex'}`}>
          {partnerId && selectedPartner ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-sand bg-white flex items-center gap-3">
                <Link to="/messages" className="md:hidden">
                  <Button variant="ghost" size="icon">
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                </Link>
                <Avatar className="h-10 w-10">
                  <AvatarImage src={getProfileImageUrl(selectedPartner.profile_image)} />
                  <AvatarFallback className="bg-forest text-white">
                    {selectedPartner.full_name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h2 className="font-medium text-forest">{selectedPartner.full_name}</h2>
                  <p className="text-sm text-gray-500">{selectedPartner.role === 'coach' ? 'Coach' : 'Member'}</p>
                </div>
                <ReportButton 
                  contentType="dm"
                  reportedUserId={selectedPartner.id}
                  reportedUserName={selectedPartner.full_name}
                  variant="ghost"
                  size="sm"
                />
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4 chat-messages">
                <div className="space-y-4 max-w-3xl mx-auto">
                  {messages.map((msg, index) => {
                    const showDate = index === 0 || 
                      formatDate(messages[index - 1].created_at) !== formatDate(msg.created_at);
                    const isSent = msg.sender_id === user?.id;

                    return (
                      <div key={msg.id}>
                        {showDate && (
                          <div className="text-center my-4">
                            <span className="text-xs text-gray-400 bg-sand/50 px-3 py-1 rounded-full">
                              {formatDate(msg.created_at)}
                            </span>
                          </div>
                        )}
                        <div className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}>
                          <div className={`message-bubble ${isSent ? 'sent' : 'received'}`} data-testid={`message-${msg.id}`}>
                            <p>{msg.content}</p>
                            <span className={`text-xs mt-1 block ${isSent ? 'text-white/70' : 'text-gray-500'}`}>
                              {formatTime(msg.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Message Input */}
              <div className="p-4 border-t border-sand bg-white">
                <form onSubmit={handleSendMessage} className="flex gap-3 max-w-3xl mx-auto">
                  <Input
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="input-base flex-1"
                    data-testid="message-input"
                  />
                  <Button 
                    type="submit" 
                    className="btn-primary"
                    disabled={!newMessage.trim() || sending}
                    data-testid="send-message-btn"
                  >
                    <Send className="h-5 w-5" />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h2 className="font-heading text-xl text-forest mb-2">Select a conversation</h2>
                <p className="text-gray-500">Choose from your existing conversations or start a new one from a coach profile.</p>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MessagesPage;
